import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "bridging-your-c-plugin-with-uxp"
    }}>{`Bridging your C++ Plugin with UXP`}</h1>
    <p>{`For developers with the intent to utilize the C++ based plugin SDK in conjunction with a UXP based plugin, the C++ SDK now includes a method to communicate with its UXP counterpart. On C++ side, we use the component's plugin name property in the plugin resource to identify plugins, and on UXP side, we use the `}<inlineCode parentName="p">{`id`}</inlineCode>{` field from the `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{` file.`}</p>
    <h2 {...{
      "id": "piuxpsuite"
    }}>{`PIUXPSuite`}</h2>
    <p>{`The PIUXPSuite communicates with UXP plugins using PIActionDescriptors. Use the ActionDescriptor suite as defined in the SDK's `}<inlineCode parentName="p">{`PIAction.h`}</inlineCode>{` whenever using this suite. To start with, grab `}<a parentName="p" {...{
        "href": "./PIUXPSuite.h"
      }}>{`PIUXPSuite.h`}</a>{` and add it to your plugin project.`}</p>
    <p>{`The signatures in `}<inlineCode parentName="p">{`PIUXPSuite.h`}</inlineCode>{` are as follows:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-cpp"
      }}>{`typedef void (*PIUXPMessageNotifier)(PIActionDescriptor descriptor);
typedef struct PsUXPSuite1
{
    SPAPI SPErr (*SendUXPMessage) (SPPluginRef selfRef, const char* uxpPluginId, PIActionDescriptor descriptor);
    SPAPI SPErr (*AddUXPMessageListener) (SPPluginRef selfRef, PIUXPMessageNotifier notifier);
    SPAPI SPErr (*RemoveUXPMessageListener) (SPPluginRef selfRef);

} PsUXPSuite1;
`}</code></pre>
    <h2 {...{
      "id": "csdk-to-uxp-messaging"
    }}>{`CSDK to UXP messaging`}</h2>
    <p><inlineCode parentName="p">{`PsUXPSuite1.SendUXPMessage`}</inlineCode>{` can be used to send messages to a UXP plugin given the plugin ID (from `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{`) and an action descriptor containing the message. `}</p>
    <h3 {...{
      "id": "includes-and-globals"
    }}>{`Includes and globals`}</h3>
    <p>{`First, make sure to include the right files and declare the globals to store the important pointers.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-cpp"
      }}>{`// Include these
#include "PIActions.h" // For PIActionDescriptor
#include "PIUXPSuite.h" // For messaging

// Your globals
SPBasicSuite * sSPBasic = NULL; // This is passed to your main function
SPPluginRef gPlugInRef = NULL; // This is passed to your main function
PsUXPSuite1* sUxpProcs = NULL; // You acquire this using sSPBasic
PSActionDescriptorProcs* sDescriptorProcs = NULL; // You acquire this using sSPBasic
`}</code></pre>
    <h3 {...{
      "id": "based-on-plugin-type"
    }}>{`Based on Plugin Type...`}</h3>
    <h4 {...{
      "id": "automation-plugins-autopluginmain"
    }}>{`Automation Plugins: `}<inlineCode parentName="h4">{`AutoPluginMain`}</inlineCode></h4>
    <p>{`For Automation plugins, the entry method is called `}<inlineCode parentName="p">{`AutoPluginMain`}</inlineCode>{` use the code below to extract the plugin reference and the basic suite.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-cpp"
      }}>{`DLLExport SPAPI SPErr AutoPluginMain(const char* caller, const char* selector, void* message) {

    SPMessageData* basicMessage = (SPMessageData*) message;
    sSPBasic = basicMessage->basic;
    gPlugInRef = basicMessage->self;
    ...
`}</code></pre>
    <h4 {...{
      "id": "filter-plugins-pluginmain"
    }}>{`Filter plugins: `}<inlineCode parentName="h4">{`PluginMain`}</inlineCode></h4>
    <p>{`Filter plugins use an entry method called `}<inlineCode parentName="p">{`PluginMain`}</inlineCode>{`, in which you can grab the basic suite and the plugin reference directly.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-cpp"
      }}>{`DLLExport MACPASCAL void PluginMain(const int16 selector,
                                    FilterRecordPtr filterRecord,
                                    intptr_t * data,
                                    int16 * result) {
    sSPBasic = filterRecord->sSPBasic;
    gPlugInRef = filterRecord->plugInRef; 
    ...
`}</code></pre>
    <h3 {...{
      "id": "acquiring-the-suites"
    }}>{`Acquiring the suites`}</h3>
    <p>{`Next up is acquiring the correct suites to create your messages with ActionDescriptors and sending them to the UXP plugin of your choice.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-cpp"
      }}>{`// in your main method
    sSPBasic->AcquireSuite(kPSUXPSuite,
                           kPSUXPSuiteVersion1,
                           (const void**)&sUxpProcs);

    sSPBasic->AcquireSuite(kPSActionDescriptorSuite,
                           kPSActionDescriptorSuiteVersion,
                           (const void**)&sDescriptorProcs);

    PIActionDescriptor desc;
    sDescriptorProcs->Make(&desc);
    sDescriptorProcs->PutString(desc, 'helo', "Hello World!");
    sDescriptorProcs->PutFloat(desc, 'fltp', 0.952);

    const char* UXP_MANIFEST_ID = "com.your.pluginId";
    sUxpProcs->SendUXPMessage(gPlugInRef, UXP_MANIFEST_ID, desc);
}
`}</code></pre>
    <h3 {...{
      "id": "on-your-uxp-plugin"
    }}>{`On your UXP plugin`}</h3>
    <p>{`Add a messaging listener using the `}<inlineCode parentName="p">{`messaging`}</inlineCode>{` API group. Any descriptors sent from a CSDK plugin to the ID of this plugin will arrive on this callback.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`let callback = (o) => { 
    console.log("Message from " + o.pluginId + ":" + o.message); 
}

require('photoshop').messaging.addSDKMessagingListener(callback);

...
// You can remove your listener using this API
require('photoshop').messaging.removeSDKMessagingListener(callback);
`}</code></pre>
    <h2 {...{
      "id": "uxp-to-csdk-messaging"
    }}>{`UXP to CSDK messaging`}</h2>
    <p>{`For communication from a UXP plugin to a C plugin, define a listener within your CPlugin, and utilize both `}<inlineCode parentName="p">{`AddUXPMessageListener`}</inlineCode>{` and `}<inlineCode parentName="p">{`RemoveUXPMessageListener`}</inlineCode>{` at the appropriate time.`}</p>
    <h3 {...{
      "id": "listener-callback"
    }}>{`Listener Callback`}</h3>
    <p>{`All messages sent to this plugin will be handled in this method`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-cpp"
      }}>{`void UXPMessageHandler(PIActionDescriptor descriptor) {
    // do something
}
`}</code></pre>
    <h3 {...{
      "id": "adding-listener"
    }}>{`Adding listener`}</h3>
    <p>{`Use the code below where you'd like to start listening to messages.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-cpp"
      }}>{`sUxpProcs->AddUXPMessageListener(gPlugInRef, UXPMessageHandler);
`}</code></pre>
    <h3 {...{
      "id": "removing-listener"
    }}>{`Removing listener`}</h3>
    <p>{`Use the code below to stop listening to messages from UXP plugins.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-cpp"
      }}>{`sUxpProcs->RemoveUXPMessageListener(gPlugInRef);
`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><em parentName="p">{`NOTE`}</em>{`: Only one notifier per plugin may be registered.`}</p>
    </blockquote>
    <h3 {...{
      "id": "sending-the-message-from-uxp"
    }}>{`Sending the message from UXP`}</h3>
    <p>{`To send messages to the C Plugin, take note of the Component Id as defined in its resouce `}<inlineCode parentName="p">{`PiPL`}</inlineCode>{`. This is the second parameter of the `}<inlineCode parentName="p">{`PIComponentProperty`}</inlineCode>{`.`}</p>
    <p>{`With this Id, use the messaging group of the photoshop package to send your message across.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`let messageContent = {
    status: "ok",
    filter: 416
};
window.require('photoshop').messaging.sendSDKPluginMessage(<YourPluginComponentId>, messageContent);
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      